import {useState} from 'react'
import {announce} from '@github-ui/aria-live'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Flash} from '@primer/react'
import {StopIcon} from '@primer/octicons-react'
import {ClockHandsIcon} from '@github-ui/custom-icons/ClockHandsIcon'
import {ForYouItem} from '../shared/ForYouItem'
import {decideChecksItem} from './shared/checks-item-decider'
import {useMergeabilityData} from './use-mergeability-data'
import {ApproveWorkflowsButton} from '@github-ui/mergebox/components/sections/checks/ApproveWorkflowsButton'
import {WorkflowApprovalSubtitle} from '@github-ui/mergebox/components/sections/checks/WorkflowApprovalSubtitle'
import {checksSectionHeading} from '@github-ui/mergebox/components/sections/checks/checks-section-header-text'
import {usePullRequestAnalytics} from '../../../../hooks/use-pull-request-analytics'
import styles from '../shared/ForYouItem.module.css'
import {OverviewMergeControl} from './shared/OverviewMergeControl'
import {OverviewUpdateBranchControl} from './shared/OverviewUpdateBranchControl'
import {isForYouRefreshEnabled} from '../shared/for-you-feature-flags'
import {useForYouActionErrors} from '../shared/use-for-you-action-errors'

// Renders when workflow runs are awaiting approval and the viewer can approve them.
// Surfaces the action to approve the pending workflow runs.
export function ApprovableChecks() {
  const {onUpdateBranchError, onQueueError, footer} = useForYouActionErrors()
  const data = useMergeabilityData()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const {sendPullRequestAnalyticsEvent} = usePullRequestAnalytics()

  const decision = decideChecksItem(data)
  if (!data || decision.kind !== 'approvable') {
    return null
  }

  const {mergeBoxPullRequest} = data
  const {workflowApproval} = decision

  return (
    <ForYouItem
      itemId="pending-checks"
      visual={{kind: 'icon', icon: ClockHandsIcon, tone: 'attention', glyphSize: 24}}
      title={checksSectionHeading.PENDING_APPROVAL}
      subText={
        <WorkflowApprovalSubtitle
          pendingWorkflowApprovalRollup={workflowApproval}
          className={styles.description}
          onClick={() =>
            sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
              item_name: 'pending-checks',
              action_name: 'learn_about_approving_workflows',
            })
          }
        />
      }
      actions={[
        {
          id: 'approve-workflows',
          content: (
            <ApproveWorkflowsButton
              headSha={mergeBoxPullRequest.headRefOid}
              onApprovalStart={() => {
                setErrorMessage(null)
                sendPullRequestAnalyticsEvent('overview.for_you_item_action', 'OVERVIEW_FOR_YOU_ITEM', {
                  item_name: 'pending-checks',
                  action_name: 'approve_workflows',
                })
              }}
              onError={error => {
                setErrorMessage(error)
                if (error) announce(error, {assertive: true})
              }}
              size="small"
            />
          ),
        },
        {
          id: 'update-branch',
          content: isForYouRefreshEnabled() ? (
            <OverviewUpdateBranchControl itemName="pending-checks" onError={onUpdateBranchError} />
          ) : null,
        },
        {
          id: 'merge-when-ready',
          content: <OverviewMergeControl data={data} itemName="pending-checks" onQueueError={onQueueError} />,
        },
      ]}
      footer={
        <>
          {footer}
          {errorMessage && (
            <Flash className="tmp-m-3" variant="danger">
              <StopIcon className="mr-2" />
              {errorMessage}
            </Flash>
          )}
        </>
      }
    />
  )
}
